import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useState } from 'react';
import { useIntl } from 'react-intl-next';
import PenIcon from '@atlaskit/icon/core/pen';
import InfoIcon from '@atlaskit/icon/utility/migration/information--info';
import { RovoIcon } from '@atlaskit/legacy-custom-icons';
import Link from '@atlaskit/link';
import Lozenge from '@atlaskit/lozenge';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Flex, Pressable, Text, xcss } from '@atlaskit/primitives';
import ProfileClient from '@atlaskit/profilecard/client';
import ProfileCardTrigger from '@atlaskit/profilecard/user';
import { N40A, N700, N900 } from '@atlaskit/theme/colors';
import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import { AiBrand } from '../../../constants';
import { onCuratedDefinitionSeeEditHistoryButtonClicked } from '../analytics';
import { ATLASSIAN_INTELLIGENCE_TRUST_LINK, ATLASSIAN_ROVO_LINK } from '../common/constants';
import { AtlassianIntelligenceIcon } from '../common/icons/atlassian-intelligence-icon';
import { messages } from './messages';
var AIFooterStyles = xcss({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingTop: 'space.050',
  paddingBottom: 'space.050',
  paddingLeft: 'space.250',
  paddingRight: 'space.250',
  borderTop: "1px solid ".concat("var(--ds-border, ".concat(N40A, ")")),
  width: '100%'
});
var ContentMayVaryWrapperStyles = xcss({
  display: 'flex',
  alignItems: 'center',
  gap: 'space.050',
  color: 'color.text.subtlest',
  font: "var(--ds-font-body-small, normal 400 11px/16px ui-sans-serif, -apple-system, BlinkMacSystemFont, \"Segoe UI\", Ubuntu, \"Helvetica Neue\", sans-serif)"
});
var BrandWrapperStyles = xcss({
  display: 'flex',
  alignItems: 'center',
  color: 'color.text.subtlest',
  font: "var(--ds-font-body-small, normal 400 11px/16px ui-sans-serif, -apple-system, BlinkMacSystemFont, \"Segoe UI\", Ubuntu, \"Helvetica Neue\", sans-serif)"
});
export var AIDefinitionFooterSection = function AIDefinitionFooterSection() {
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  var _useAIAnswerContext = useAIAnswerContext(),
    brand = _useAIAnswerContext.brand;
  return /*#__PURE__*/React.createElement(Box, {
    xcss: AIFooterStyles
  }, /*#__PURE__*/React.createElement(Box, {
    xcss: ContentMayVaryWrapperStyles
  }, brand === AiBrand.ROVO && fg('platform_search_ai_rovo_beta_label_off') ? null : /*#__PURE__*/React.createElement(Lozenge, null, formatMessage(messages.definition_footer_beta_status_label)), /*#__PURE__*/React.createElement(InfoIcon, {
    label: "",
    color: "var(--ds-icon-subtle, #626F86)",
    LEGACY_size: "small"
  }), /*#__PURE__*/React.createElement(Link, {
    rel: "noopener noreferrer",
    appearance: "subtle",
    href: ATLASSIAN_INTELLIGENCE_TRUST_LINK
  }, formatMessage(messages.definition_footer_content_quality_short))), /*#__PURE__*/React.createElement(Box, {
    xcss: BrandWrapperStyles
  }, brand === AiBrand.ROVO ? /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center",
    gap: "space.025"
  }, /*#__PURE__*/React.createElement(Link, {
    rel: "noopener noreferrer",
    appearance: "subtle",
    href: ATLASSIAN_ROVO_LINK
  }, "Rovo"), /*#__PURE__*/React.createElement(RovoIcon, {
    label: "",
    primaryColor: "var(--ds-icon-subtle, #626F86)"
  })) : /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center",
    gap: "space.025"
  }, formatMessage(messages.definition_footer_powered_by_AI), /*#__PURE__*/React.createElement(AtlassianIntelligenceIcon, {
    greyed: true,
    size: "large"
  }))));
};
var EditedByFooterStyles = xcss({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingTop: 'space.150',
  paddingBottom: 'space.150',
  paddingLeft: 'space.250',
  paddingRight: 'space.250',
  borderTop: "1px solid ".concat("var(--ds-border, ".concat(N40A, ")"))
});
var EditedByWrapper = xcss({
  display: 'flex',
  background: "var(--ds-background-neutral, ".concat(N900, ")"),
  paddingLeft: 'space.050',
  paddingRight: 'space.050',
  borderRadius: '24px',
  marginLeft: 'space.050',
  cursor: 'pointer',
  ':hover': {
    background: "var(--ds-background-neutral-hovered, ".concat(N700, ")")
  }
});
var DefinedByWrapper = xcss({
  marginLeft: 'space.100'
});
var ActiveEditedByWrapper = xcss({
  display: 'flex',
  background: "var(--ds-background-neutral-hovered, ".concat(N700, ")"),
  paddingLeft: 'space.050',
  paddingRight: 'space.050',
  borderRadius: '24px',
  marginLeft: 'space.050',
  cursor: 'pointer'
});
var EditHistoryWrapper = xcss({
  display: 'flex',
  font: 'font.body.small',
  color: 'color.link',
  paddingLeft: 'space.300',
  cursor: 'pointer',
  ':hover': {
    textDecoration: 'underline'
  }
});
var DEFAULT_GRAPHQL_DIRECTORY_ENDPOINT = '/gateway/api/directory/graphql';
var profileClient = new ProfileClient({
  url: DEFAULT_GRAPHQL_DIRECTORY_ENDPOINT
});
export var EditedDefinitionFooterSection = function EditedDefinitionFooterSection(_ref) {
  var setShowEditHistoryModal = _ref.setShowEditHistoryModal;
  var _useIntl2 = useIntl(),
    formatMessage = _useIntl2.formatMessage;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    editorActive = _useState2[0],
    setEditorActive = _useState2[1];
  var _useAIAnswerContext2 = useAIAnswerContext(),
    answerEditor = _useAIAnswerContext2.answerEditor,
    commonAttributes = _useAIAnswerContext2.commonAttributes,
    fireAnalyticsEvent = _useAIAnswerContext2.fireAnalyticsEvent;
  var cloudId = commonAttributes.cloudId;
  if (!answerEditor) {
    return null; // This line should not be reachable since the component is only rendered when answerEditor is defined
  }
  return /*#__PURE__*/React.createElement(Box, {
    xcss: EditedByFooterStyles
  }, /*#__PURE__*/React.createElement(Flex, {
    gap: "space.100",
    alignItems: "start",
    direction: "column"
  }, /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(PenIcon, {
    label: "",
    color: "var(--ds-icon-subtle, ".concat(N700, ")")
  }), /*#__PURE__*/React.createElement(Box, {
    xcss: DefinedByWrapper
  }, /*#__PURE__*/React.createElement(Text, null, formatMessage(messages.definition_footer_defined_by_text))), /*#__PURE__*/React.createElement(ProfileCardTrigger, {
    userId: answerEditor.id,
    cloudId: cloudId,
    resourceClient: profileClient,
    trigger: "click",
    onVisibilityChange: setEditorActive
  }, /*#__PURE__*/React.createElement(Box, {
    xcss: editorActive ? ActiveEditedByWrapper : EditedByWrapper
  }, "@", answerEditor.name))), fg('custom-interactive-elements-not-keyboard-focusable') ? /*#__PURE__*/React.createElement(Pressable, {
    backgroundColor: "color.background.neutral.subtle",
    xcss: EditHistoryWrapper,
    onClick: function onClick() {
      fireAnalyticsEvent(onCuratedDefinitionSeeEditHistoryButtonClicked(commonAttributes));
      setShowEditHistoryModal(true);
    }
  }, formatMessage(messages.definition_footer_see_edit_history)) :
  /*#__PURE__*/
  // eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
  React.createElement(Box, {
    xcss: EditHistoryWrapper,
    onClick: function onClick() {
      fireAnalyticsEvent(onCuratedDefinitionSeeEditHistoryButtonClicked(commonAttributes));
      setShowEditHistoryModal(true);
    }
  }, formatMessage(messages.definition_footer_see_edit_history))));
};